// Tiny fetch wrapper that:
// - sends cookies (refresh token)
// - injects Bearer access token
// - auto-refreshes the access token on 401 once

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function refreshOnce(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!r.ok) return null;
        const data = (await r.json()) as { accessToken: string };
        accessToken = data.accessToken;
        return accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Fastify's default error shape is { statusCode, error: 'Bad Request', message: 'real reason' }
// where `error` is the HTTP status name. Our own routes return { error: 'human message' } only.
// Prefer `message` when it exists, fall back to `error`, then HTTP status.
function extractErrorMessage(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const message = obj.message;
    if (typeof message === 'string' && message.length > 0) return message;
    const error = obj.error;
    if (typeof error === 'string' && error.length > 0) return error;
  }
  return `HTTP ${status}`;
}

export type RequestOpts = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  noRefresh?: boolean;
};

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const hasBody = opts.body !== undefined;
  const headers: Record<string, string> = {};
  // Only set Content-Type when we actually send a body. Fastify v5 rejects
  // empty payloads with `Content-Type: application/json` ("Body cannot be empty").
  if (hasBody) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const doFetch = async () =>
    fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: hasBody ? JSON.stringify(opts.body) : undefined,
      credentials: 'include',
      signal: opts.signal,
    });

  let res = await doFetch();

  if (res.status === 401 && !opts.noRefresh) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      headers.Authorization = `Bearer ${refreshed}`;
      res = await doFetch();
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const msg = extractErrorMessage(data, res.status);
    throw new ApiError(res.status, data, msg);
  }

  return data as T;
}
